/* ============================================================================
   Stickr Lab — Shopify Cart Adapter  (assets/sl-cart-adapter.js)

   The ONLY Shopify-aware module. Translates the canonical, Shopify-independent
   Builder payload (from sl-builder-core.js) into a Shopify Cart AJAX line item +
   custom properties, and a display record for the cart UI.

   It contains NO pricing, layout, or upload logic (those live in the engines) and
   invents NO production fields — every property maps to a field already in the
   payload. The Builder/core/engines never import Shopify; Shopify consumes them.

   Pricing model (D-002): the approved $0.01-variant / quantity-as-price workaround.
   The Shopify line-item `quantity` is the price in cents of a $0.01 variant; the real
   sticker count travels in a custom property. Die-cut AND Sticker Sheets are orderable
   (D-050 — reverses D-024/D-038): sheets ride the same transport, priced by total stickers
   delivered (SLPricing.sheet = die-cut × 0.92, D-033).

   PROPERTY MAP (payload → Shopify line-item property). Sheet-only / die-cut-only rows branch
   on productType so one map serves both products:
     Visible (shown in cart/checkout):
       Product                  ← 'Custom Sticker' | 'Custom Sticker Sheets'
       Sheet Size               ← sheet.sheetSize.label            (sheets only)
       Sticker Size             ← dimensions (in)                  (sheets only)
       Size                     ← dimensions (in)                  (die-cut only)
       Material / Finish / Border ← options.*
       Sheets                   ← sheet.sheetQuantity              (sheets only)
       Stickers per sheet       ← sheet.stickersPerSheet           (sheets only)
       Estimated total stickers ← sheet.totalStickers              (sheets only)
       Quantity                 ← quantity (real sticker count)    (die-cut only)
       Artwork                  ← artwork.fileName
     Hidden (leading underscore — internal/production):
       _builder_version ← builderVersion
       _product_type    ← productType
       _sheet_size_id / _sheet_qty / _stickers_per_sheet / _total_stickers ← sheet.* (sheets only)
       _artwork_ref     ← artwork.reference
       _artwork_format  ← artwork.format
       _effective_dpi   ← effectiveDpi
       _unit_price      ← pricing.perUnit
       _line_price      ← pricing.price
       _price_source    ← pricing.source
       _warnings        ← warnings[].code (comma-joined)
   ============================================================================ */
(function (root) {
  'use strict';

  var config = {
    // The $0.01 storefront variant used for the quantity-as-price workaround (D-002).
    // Supplied by the store — NOT invented here. Placeholder until wired to the real variant.
    oneCentVariantId: null
  };

  // Is this payload a customer-configured Sticker Sheet (D-050)?
  function isSheet(p) { return p && p.productType === 'sheet'; }
  // The finished (trimmed) sheet the layout engine worked out, or null (D-179).
  function finished(p) { return (isSheet(p) && p.sheet && p.sheet.finished && p.sheet.finished.widthIn > 0) ? p.sheet.finished : null; }
  /* CART GRAPHICS (D-177): a separate product on the same transport. Every row
     it adds below returns null for a sticker or a sheet, so their properties
     are byte-for-byte what they were. */
  var CART_GRAPHICS = 'cart_graphics';
  function isCartGraphics(p) { return p && p.productType === CART_GRAPHICS; }
  function cg(p) { return (isCartGraphics(p) && p.cartGraphics) ? p.cartGraphics : null; }
  function cgField(p, k) { var c = cg(p); return (c && c[k] != null && c[k] !== '') ? c[k] : null; }
  /* May the finish be named on this cart graphic? The builder says so on the
     payload; an older payload that says nothing is treated as named. */
  function cgFinishNamed(p) { var c = cg(p); return !(c && c.finishConfirmed === false); }
  function cap(v) { v = String(v == null ? '' : v); return v ? v.charAt(0).toUpperCase() + v.slice(1) : v; }
  // Sheet-derived getters read the canonical sheet result (from SLSheets, via the payload) — never
  // recomputed or invented here. Null when absent so the property is simply omitted.
  function sheetSizeLabel(p) { return (p.sheet && p.sheet.sheetSize) ? p.sheet.sheetSize.label : null; }
  // One kiss-cut sticker on its own trimmed backing (D-198): each "sheet" is one sticker.
  function isSingle(p)       { return !!(p.sheet && (p.sheet.single || (p.sheet.sheetSize && p.sheet.sheetSize.single))); }
  // The size words for display: '8.5 × 10 Sheet', or 'Single sticker'.
  function sheetWords(label, single) { return single ? label : (label.replace(/ in$/, '') + ' Sheet'); }
  function sheetQty(p)       { return (p.sheet && p.sheet.sheetQuantity != null) ? p.sheet.sheetQuantity : null; }
  function perSheet(p)       { return (p.sheet && p.sheet.stickersPerSheet != null) ? p.sheet.stickersPerSheet : null; }
  function totalStickers(p)  { return (p.sheet && p.sheet.totalStickers != null) ? p.sheet.totalStickers : null; }
  function dims(p)           { return p.dimensions.widthIn + ' × ' + p.dimensions.heightIn + ' in'; }

  // Documented, testable mapping (visible + hidden). Each entry maps a Shopify property key to a pure
  // getter over the canonical payload; a getter returning null/''/undefined omits that property. No
  // invented fields. Sheet-only and die-cut-only rows branch on isSheet() so one map serves both
  // products: Sticker Sheets read Sheet Size / Sticker Size / Sheets / per-sheet / total, while die-cut
  // keeps its original Size / Quantity. Cart clearly says "Custom Sticker Sheets" (D-050).
  /**
   * One human-readable line describing the whole design, for a person reading an order.
   *
   * Deliberately plain prose rather than JSON: the audience is whoever is holding the
   * job ticket or answering the support email, not a parser. The structured version of
   * the same information is already in the individual properties and the config file.
   *
   *   Custom Sticker · 3" × 3" · vinyl matte · no border · qty 100
   *   Custom Sticker Sheets · 8.5x10 sheet × 10 · 3" × 3" stickers · ~60 stickers
   */
  function designSummary(p) {
    if (!p) return null;
    var parts = [];
    if (isCartGraphics(p)) {
      /* Cart Graphics · graphic only · 34 × 19 in · Rectangle · matte vinyl, no laminate · qty 1 */
      parts.push('Cart Graphics', 'graphic only', dims(p));
      if (cgField(p, 'shapeLabel')) parts.push(cgField(p, 'shapeLabel'));
      parts.push((cgFinishNamed(p) ? p.options.finish + ' vinyl' : 'white vinyl') + ', no laminate');
      if (cgField(p, 'borderSummary')) parts.push(cgField(p, 'borderSummary'));
      parts.push('qty ' + p.quantity);
      return parts.join(' · ');
    }
    if (isSheet(p)) {
      parts.push('Custom Sticker Sheets');
      var sl = sheetSizeLabel(p); if (sl) parts.push(isSingle(p) ? (sl + ' × ' + sheetQty(p) + ', each on its own backing') : (sl + ' sheet × ' + sheetQty(p)));
      parts.push(dims(p) + ' stickers');
      var tot = totalStickers(p); if (tot) parts.push('~' + tot + ' stickers');
    } else {
      parts.push('Custom Sticker');
      parts.push(dims(p));
      parts.push('qty ' + p.quantity);
    }
    var o = p.options || {};
    var finish = [o.material, o.finish].filter(Boolean).join(' ');
    if (finish) parts.push(finish);
    if (o.border && o.border !== 'none') parts.push(o.border + ' border');
    return parts.join(' · ');
  }

  var PROPERTY_MAP = [
    { key: 'Product',                   hidden: false, from: function (p) { return isCartGraphics(p) ? 'Cart Graphics' : isSheet(p) ? 'Custom Sticker Sheets' : 'Custom Sticker'; } },
    /* Says what is NOT in the box, on the order itself: no cart, no sign board,
       no installation. */
    { key: 'Includes',                  hidden: false, from: function (p) { return isCartGraphics(p) ? 'Graphic only' : null; } },
    { key: 'Sheet Size',                hidden: false, from: function (p) { return isSheet(p) ? sheetSizeLabel(p) : null; } },
    /* The sheet the customer picked is the NOMINAL size ('Sheet Size', above). This is the
       sheet they receive: trimmed closer to the stickers (D-179). Both are stated, by name. */
    { key: 'Finished Sheet Size',       hidden: false, from: function (p) { var f = finished(p); return f ? f.label + (isSingle(p) ? ' (backing)' : f.trimmed ? ' (trimmed)' : '') : null; } },
    { key: 'Sticker Size',              hidden: false, from: function (p) { return isSheet(p) ? dims(p) : null; } },
    { key: 'Size',                      hidden: false, from: function (p) { return isSheet(p) ? null : dims(p); } },
    { key: 'Material',                  hidden: false, from: function (p) { return p.options.material; } },
    { key: 'Finish',                    hidden: false, from: function (p) { return (isCartGraphics(p) && !cgFinishNamed(p)) ? null : p.options.finish; } },
    /* The finish above is the VINYL's own on this product — there is no
       laminate, and the order says so rather than leaving it to be inferred. */
    { key: 'Vinyl Finish',              hidden: false, from: function (p) { return (isCartGraphics(p) && cgFinishNamed(p)) ? cap(p.options.finish) : null; } },
    { key: 'Laminate',                  hidden: false, from: function (p) { return isCartGraphics(p) ? 'None' : null; } },
    { key: 'Shape',                     hidden: false, from: function (p) { return cgField(p, 'shapeLabel'); } },
    { key: 'Border',                    hidden: false, from: function (p) { return p.options.border; } },
    { key: 'Border Color',              hidden: false, from: function (p) { return cgField(p, 'borderColor'); } },
    { key: 'Border Thickness',          hidden: false, from: function (p) { var l = cgField(p, 'borderLevel'); return l ? String(l).charAt(0).toUpperCase() + String(l).slice(1) : null; } },
    { key: 'Border Width',              hidden: false, from: function (p) { var w = cgField(p, 'borderWidthIn'); return w != null ? w + ' in' : null; } },
    { key: 'Sheets',                    hidden: false, from: function (p) { return isSheet(p) ? sheetQty(p) : null; } },
    { key: 'Stickers per sheet',        hidden: false, from: function (p) { return isSheet(p) ? perSheet(p) : null; } },
    { key: 'Estimated total stickers',  hidden: false, from: function (p) { return isSheet(p) ? totalStickers(p) : null; } },
    { key: 'Quantity',                  hidden: false, from: function (p) { return isSheet(p) ? null : p.quantity; } },
    { key: 'Artwork',                   hidden: false, from: function (p) { return p.artwork.fileName; } },
    /* ---- durable production record (D-056) ----
       These four are PLAIN TEXT on purpose, alongside the three uploaded files.

       The files are the real assets, but their durability rests on Shopify CDN
       retention, which Shopify does not contractually guarantee. Text properties on an
       order are unambiguous and permanent, so if a file URL ever failed you would still
       know exactly which design this was, what it was, and which code produced it —
       enough to find the artwork by other means instead of ringing the customer.

       `Design ID` is deliberately VISIBLE: the customer can quote it in a support email
       and production can read it off the packing slip. */
    { key: 'Design ID',                 hidden: false, from: function (p) { return p.designId || null; } },
    { key: '_design_summary',           hidden: true,  from: function (p) { return designSummary(p); } },
    { key: '_schema_version',           hidden: true,  from: function (p) { return p.schemaVersion; } },
    { key: '_builder_version',          hidden: true,  from: function (p) { return p.builderVersion; } },
    { key: '_product_type',             hidden: true,  from: function (p) { return p.productType; } },
    /* Production detail, hidden: the customer flow never names a supplier code. */
    { key: '_vinyl_family',             hidden: true,  from: function (p) { return cgField(p, 'vinylFamily'); } },
    { key: '_laminate',                 hidden: true,  from: function (p) { return isCartGraphics(p) ? 'none' : null; } },
    /* Not shown to the customer: what production intended while the finish is unconfirmed. */
    { key: '_vinyl_finish_intended',    hidden: true,  from: function (p) { return (isCartGraphics(p) && !cgFinishNamed(p)) ? (p.options.finish + ' (not confirmed)') : null; } },
    { key: '_shape',                    hidden: true,  from: function (p) { return cgField(p, 'shape'); } },
    { key: '_background_removed',       hidden: true,  from: function (p) { return isCartGraphics(p) ? ((p.transform && p.transform.backgroundRemoved) ? 'yes' : 'no') : null; } },
    /* REFERENCE ONLY. Where the customer placed the graphic on the cart they
       measured. It is not a production measurement and implies no install. */
    { key: '_cart_preview',             hidden: true,  from: function (p) { return cgField(p, 'previewSummary'); } },
    { key: '_sheet_size_id',            hidden: true,  from: function (p) { return (isSheet(p) && p.sheet && p.sheet.sheetSize) ? p.sheet.sheetSize.id : null; } },
    { key: '_sheet_qty',                hidden: true,  from: function (p) { return isSheet(p) ? sheetQty(p) : null; } },
    { key: '_stickers_per_sheet',       hidden: true,  from: function (p) { return isSheet(p) ? perSheet(p) : null; } },
    { key: '_total_stickers',           hidden: true,  from: function (p) { return isSheet(p) ? totalStickers(p) : null; } },
    { key: '_sheet_finished_w',         hidden: true,  from: function (p) { var f = finished(p); return f ? f.widthIn : null; } },
    { key: '_sheet_finished_h',         hidden: true,  from: function (p) { var f = finished(p); return f ? f.heightIn : null; } },
    { key: '_sheet_corner_radius',      hidden: true,  from: function (p) { var f = finished(p); return f ? f.cornerRadiusIn : null; } },
    { key: '_artwork_ref',              hidden: true,  from: function (p) { return p.artwork.reference; } },
    { key: '_artwork_format',           hidden: true,  from: function (p) { return p.artwork.format; } },
    { key: '_effective_dpi',            hidden: true,  from: function (p) { return p.effectiveDpi; } },
    /* Present only when the customer was actually asked and answered. HQ reads
       it the way it reads _effective_dpi. */
    { key: '_artwork_quality_ack',      hidden: true,  from: function (p) { return p.qualityAck; } },
    { key: '_unit_price',               hidden: true,  from: function (p) { return p.pricing ? p.pricing.perUnit : null; } },
    { key: '_line_price',               hidden: true,  from: function (p) { return p.pricing ? p.pricing.price : null; } },
    { key: '_price_source',             hidden: true,  from: function (p) { return p.pricing ? p.pricing.source : null; } },
    { key: '_warnings',                 hidden: true,  from: function (p) { return (p.warnings || []).map(function (w) { return w.code; }).join(','); } }
  ];

  function properties(payload) {
    var out = {};
    for (var i = 0; i < PROPERTY_MAP.length; i++) {
      var m = PROPERTY_MAP[i], v = m.from(payload);
      if (v !== null && v !== undefined && v !== '') out[m.key] = v;
    }
    return out;
  }

  // Is this payload orderable into the cart? Priced and ready. Die-cut AND Sticker Sheets are now both
  // orderable (D-050 — reverses the D-024/D-038 sheet-checkout deferral): sheets carry a real price
  // (SLPricing.sheet = die-cut × 0.92 on the total sticker count) and ride the SAME $0.01-variant
  // transport. An unpriced or not-ready payload (either product) is still refused.
  function orderability(payload) {
    if (!payload) return { orderable: false, reason: 'no-payload' };
    if (payload.pricingAvailable === false)
      return { orderable: false, reason: 'pricing-unavailable' };
    if (!payload.pricing || payload.pricing.error || !(payload.pricing.price > 0))
      return { orderable: false, reason: 'no-price' };
    if (payload.readiness && payload.readiness.canOrder === false)
      return { orderable: false, reason: (payload.readiness.reasons && payload.readiness.reasons[0]) || 'not-ready' };
    return { orderable: true, reason: 'ok' };
  }

  /* ---- payload → Shopify Cart AJAX line item ----
     opts.variantId overrides config.oneCentVariantId. Returns
     { orderable:false, reason } when the payload cannot be added. */
  function toLineItem(payload, opts) {
    opts = opts || {};
    var ord = orderability(payload);
    if (!ord.orderable) return { orderable: false, reason: ord.reason };
    var variantId = opts.variantId != null ? opts.variantId : config.oneCentVariantId;
    // $0.01 variant, quantity-as-price (D-002): line quantity = price in cents.
    var qtyAsPrice = Math.round(payload.pricing.price * 100);
    return {
      orderable: true,
      id: variantId,                 // null until the real $0.01 variant is wired (store-provided)
      quantity: qtyAsPrice,
      properties: properties(payload)
    };
  }

  /* ---- payload → cart display record (for the cart UI / mock cart) ---- */
  function toCartDisplay(payload) {
    if (!payload) return null;
    var o = payload.options || {};
    var sheet = isSheet(payload);
    if (isCartGraphics(payload)) {
      return {
        name: 'Cart Graphics',
        thumb: payload.artwork ? payload.artwork.reference : null,
        specs: ['Graphic only', dims(payload), cap(o.finish) + ' vinyl · No laminate',
                (o.border && o.border !== 'none') ? ('Border: ' + o.border) : null].filter(Boolean),
        qty: payload.quantity, qtyUnit: 'graphics',
        ea: payload.pricing ? payload.pricing.perUnit : null,
        total: payload.pricing ? payload.pricing.price : null,
        pricingAvailable: payload.pricingAvailable !== false
      };
    }
    var specs = sheet
      ? [
          sheetSizeLabel(payload) ? sheetWords(sheetSizeLabel(payload), isSingle(payload)) : null,
          dims(payload).replace(/ in$/, '') + ' Stickers',
          [o.material, o.finish].filter(Boolean).join(' · '),
          (o.border && o.border !== 'none') ? ('Border: ' + o.border) : null,
          sheetQty(payload) != null ? ((isSingle(payload) ? 'Stickers: ' : 'Sheets: ') + sheetQty(payload)) : null,
          (perSheet(payload) != null && !isSingle(payload)) ? ('Stickers per sheet: ' + perSheet(payload)) : null,
          totalStickers(payload) != null ? ('Estimated total stickers: ' + totalStickers(payload)) : null
        ].filter(Boolean)
      : [
          dims(payload),
          [o.material, o.finish].filter(Boolean).join(' · '),
          (o.border && o.border !== 'none') ? ('Border: ' + o.border) : null
        ].filter(Boolean);
    return {
      name: sheet ? 'Custom Sticker Sheets' : 'Custom Sticker',
      thumb: payload.artwork ? payload.artwork.reference : null,
      specs: specs,
      // Read-only sheet quantity for sheets (number of sheets); real sticker count for die-cut (D-050/D-035).
      qty: sheet ? sheetQty(payload) : payload.quantity,
      qtyUnit: sheet ? 'sheets' : 'stickers',
      ea: payload.pricing ? payload.pricing.perUnit : null,
      total: payload.pricing ? payload.pricing.price : null,
      pricingAvailable: payload.pricingAvailable !== false
    };
  }

  /* ---- Shopify cart line item → custom cart UI display record ----
     Inverse of toLineItem's PROPERTY_MAP. Pure (no DOM/network) so the cart UI can render
     from Shopify's real cart (the source of truth) while the ugly quantity-as-price transport
     stays hidden: price comes from Shopify's authoritative line total, the real sticker count
     from the `Quantity` property. */
  function fromLineItem(item) {
    if (!item) return null;
    var p = item.properties || {};
    var sheet = p._product_type === 'sheet';
    var cartG = p._product_type === CART_GRAPHICS;
    var lineCents = (typeof item.final_line_price === 'number') ? item.final_line_price
                  : (typeof item.line_price === 'number') ? item.line_price : null;
    // Real sticker count (NOT the cents quantity): die-cut uses Quantity; sheets use the total-stickers prop.
    var stickerSrc = sheet ? (p['Estimated total stickers'] != null ? p['Estimated total stickers'] : p._total_stickers)
                           : p.Quantity;
    var stickerQty = stickerSrc != null ? parseInt(stickerSrc, 10) : null;
    if (stickerQty != null && isNaN(stickerQty)) stickerQty = null;
    var sheetQtyN = null;
    if (sheet) {
      var sq = (p.Sheets != null) ? p.Sheets : p._sheet_qty;
      sheetQtyN = sq != null ? parseInt(sq, 10) : null;
      if (sheetQtyN != null && isNaN(sheetQtyN)) sheetQtyN = null;
    }
    var unit = (p._unit_price != null && p._unit_price !== '') ? Number(p._unit_price) : null;
    if (unit != null && isNaN(unit)) unit = null;
    var specs = [];
    if (sheet) {
      if (p['Sheet Size']) specs.push(sheetWords(String(p['Sheet Size']), p._sheet_size_id === 'single'));
      if (p['Sticker Size']) specs.push(String(p['Sticker Size']).replace(/ in$/, '') + ' Stickers');
    } else if (p.Size) {
      specs.push(String(p.Size));
    }
    var mf = [p.Material, p.Finish].filter(Boolean).join(' · ');
    if (cartG) {
      /* The cart says what the line IS before anything else: the graphic, and
         only the graphic. */
      specs.unshift('Graphic only');
      if (p.Shape) specs.push(String(p.Shape));
      mf = (p['Vinyl Finish'] ? String(p['Vinyl Finish']) + ' vinyl' : 'Vinyl') + ' · No laminate';
    }
    if (mf) specs.push(mf);
    if (p.Border && p.Border !== 'none') specs.push('Border: ' + p.Border);
    if (sheet) {
      if (sheetQtyN != null) specs.push('Sheets: ' + sheetQtyN);
      if (p['Stickers per sheet'] != null) specs.push('Stickers per sheet: ' + p['Stickers per sheet']);
      if (stickerQty != null) specs.push('Estimated total stickers: ' + stickerQty);
    }
    /* Thumbnail + production sources (stays PURE — no DOM or browser storage here).

       `previewUrl` / `originalUrl` / `configUrl` are Shopify CDN URLs: the Builder
       attached these as FILE properties, Shopify uploaded them and replaced each value
       with a URL (D-056). They are durable, cross-device, and are what production
       actually retrieves from the order.

       The legacy browser-storage handoff id is GONE from this shape, and its
       removal is the point. It was a `pv_...` key into the SHOPPER'S OWN
       localStorage: same-origin, same-device, same-browser, evicted by ITP
       after a week. Nothing server-side could ever resolve it, so carrying it
       beside three durable CDN URLs made an unrecoverable reference look like
       an asset. Live order #1012 shipped with one and no artwork at all.

       A cart line either has durable URLs or it has no assets. There is no
       third state, and this shape no longer implies one. */
    function urlProp(v) {
      if (v == null || v === '') return null;
      var s = String(v);
      return /^(https?:)?\/\//.test(s) ? s : null;      // only ever a real URL, never a raw value
    }
    var previewUrl    = urlProp(p._artwork_preview);
    var originalUrl   = urlProp(p._artwork_original);
    var configUrl     = urlProp(p._design_config);
    /* The production SVG — the file the operator actually makes the order
       from. Read here so the cart carries it as a fact rather than leaving
       every consumer to re-resolve the property. Null on older lines, which is
       what keeps existing carts working unchanged. */
    var productionUrl = urlProp(p._production_artwork);
    var artworkRef = (p._artwork_ref != null && p._artwork_ref !== '') ? String(p._artwork_ref) : null;
    return {
      key: item.key || null,                                  // Shopify line key (remove/change)
      name: cartG ? 'Cart Graphics' : (p.Product || item.product_title || (sheet ? 'Custom Sticker Sheets' : 'Custom Sticker')),
      productType: p._product_type || null,
      stickerQty: stickerQty,                                 // real sticker count (NOT the cents quantity)
      /* What one of them is called. The cart must not label a six-foot
         graphic "1 Sticker". */
      unitNoun: cartG ? 'Graphic' : 'Sticker',
      sheetQty: sheetQtyN,                                    // number of sheets (sheets only; read-only, D-035)
      specs: specs,
      linePrice: lineCents != null ? Math.round(lineCents) / 100 : null,   // Shopify authoritative $ total
      unitPrice: unit,
      artwork: p.Artwork || null,                             // artwork file NAME (display text)
      designId: p['Design ID'] || null,                       // durable id, also plain text on the order
      previewUrl: previewUrl,                                 // durable Shopify CDN preview (D-056)
      originalUrl: originalUrl,                               // durable original file — the recovery asset
      productionUrl: productionUrl,                           // durable production SVG — what production opens
      configUrl: configUrl,                                   // durable canonical config json
      artworkRef: artworkRef                                  // direct image URL if one was kept (else null)
    };
  }

  var API = {
    version: 'cart-adapter-v1',
    config: config,
    PROPERTY_MAP: PROPERTY_MAP,
    properties: properties,
    orderability: orderability,
    toLineItem: toLineItem,
    toCartDisplay: toCartDisplay,
    fromLineItem: fromLineItem
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  if (root) root.SLCartAdapter = API;
})(typeof window !== 'undefined' ? window : null);
